import { createNode, formFieldTypeToDataType, createOptional } from '@glueapp/graph-types';
import type { DataType, DataValue, FormElement, FormField, NodeInstanceDataPort } from '@glueapp/graph-types';

export const FormTrigger = createNode({
    id: 'FormTrigger',
    name: 'Form',
    description: 'Trigger a graph when a form is submitted',
    aiDescription: [
        'Triggers when a user submits a form. The output ports are defined by the form fields configured in the UI.',
        'computeShape preserves output ports from preResolvedOutputPorts (persisted from the form builder UI).',
        'The "All Fields" output port always exists and contains all submitted fields as an open object.',
        'Additional output ports (one per form field) are added by the form builder and persisted via preResolvedOutputPorts.',
        'This node has no input ports and no shape values to set via CLI — form fields are configured through the UI.',
    ].join('\n'),
    category: 'Trigger',
    color: '#808080',
    isAsync: false,
    triggerType: 'Form',
    triggerEventIdentifier: 'formSubmitted',
    hideInherentInPort: true,
    transientStateKeys: ['formId'],
    inPorts: {},
    outPorts: {
        'All Fields': { kind: 'object', properties: {}, restriction: 'open' },
    },
    computeShape: input => {
        const fields = (input.state?.fields as unknown as FormElement[]) || [];
        if (fields.length > 0) {
            const properties: Record<string, DataType> = {};
            const fieldPorts: NodeInstanceDataPort[] = [];
            for (const element of fields) {
                if (element.type === 'separator') continue;
                const field = element as FormField;
                const portType = formFieldTypeToDataType(field.type, field.options);
                const finalType = field.optional ? createOptional(portType) : portType;
                properties[field.label] = finalType;
                fieldPorts.push({ name: field.label, type: finalType });
            }
            return {
                inPorts: input.nodeInPorts,
                outPorts: [
                    { name: 'All Fields', type: { kind: 'object' as const, properties, restriction: 'exact' as const } },
                    ...fieldPorts,
                ],
            };
        }
        // Fallback for backward compat (no fields in state yet)
        return {
            inPorts: input.nodeInPorts,
            outPorts: input.preResolvedOutputPorts.length > 0 ? input.preResolvedOutputPorts : input.nodeOutPorts,
        };
    },
    execute: input => {
        const payload = input.payload as { fields: Record<string, unknown> } | undefined;
        const fields = payload?.fields ?? {};
        const output: { 'All Fields': DataValue; [key: string]: DataValue } = { 'All Fields': fields as DataValue };
        for (const [label, value] of Object.entries(fields)) {
            output[label] = value as DataValue;
        }
        return { output };
    },
});
